import { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem, FormControl,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Alert,
  Switch, Tooltip, CircularProgress, Stack, InputAdornment, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { api } from '../../services/api';
import type { DatalonSalaryType, DatalonComputeRule, DatalonSourceKind, DatalonDriverRate, DriverMapping } from '../../types';

interface Props {
  driverMappings: DriverMapping[];
  onMappingChange?: (driverId: string, field: 'datalonEmployeeNumber', value: string) => void;
  onSaveMappings?: () => Promise<void> | void;
  mappingsDirty?: boolean;
  savingMappings?: boolean;
}

const COMPUTE_LABELS: Record<DatalonComputeRule, string> = {
  WORK_HOURS: 'Timer fra perioden',
  FIXED_PER_PERIOD: 'Fast antal pr. periode',
  MANUAL: 'Manuel indtastning (springes over i auto-eksport)',
};

const SOURCE_KIND_LABELS: Record<DatalonSourceKind, string> = {
  ALL: 'Alle timer',
  NORMAL: 'Kun normal-timer',
  OT1: 'Kun overtid 50%',
  OT2: 'Kun overtid 100%',
  EVENING: 'Kun aftentimer',
  NIGHT: 'Kun nattimer',
  SICK: 'Kun sygdoms-timer',
  HOLIDAY: 'Kun feriefri-timer',
};

const num = (v: string | number | null | undefined) =>
  v === null || v === undefined ? '' : typeof v === 'number' ? String(v) : v;

export function DatalonSettings({ driverMappings, onMappingChange, onSaveMappings, mappingsDirty, savingMappings }: Props) {
  const [salaryTypes, setSalaryTypes] = useState<DatalonSalaryType[]>([]);
  const [driverRates, setDriverRates] = useState<DatalonDriverRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local edits buffer (key: `${driverId}|${salaryTypeId}` → value string)
  const [rateEdits, setRateEdits] = useState<Record<string, string>>({});

  const reload = async () => {
    setLoading(true);
    try {
      const [types, rates] = await Promise.all([
        api.getDatalonSalaryTypes(),
        api.getDatalonDriverRates(),
      ]);
      setSalaryTypes(types);
      setDriverRates(rates);
      setError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Kunne ikke hente Dataløn-konfiguration';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const activeTypes = useMemo(() => salaryTypes.filter(t => t.isActive), [salaryTypes]);

  const rateKey = (driverId: string, salaryTypeId: string) => `${driverId}|${salaryTypeId}`;
  const getDriverRate = (driverId: string, salaryTypeId: string): string => {
    const key = rateKey(driverId, salaryTypeId);
    if (key in rateEdits) return rateEdits[key];
    const existing = driverRates.find(r => r.driverId === driverId && r.salaryTypeId === salaryTypeId);
    return existing ? num(existing.rate) : '';
  };

  const setDriverRateEdit = (driverId: string, salaryTypeId: string, value: string) => {
    setRateEdits(prev => ({ ...prev, [rateKey(driverId, salaryTypeId)]: value }));
  };

  // ============================================================
  // Lønart-katalog actions
  // ============================================================
  // Nye rækker er lokale kladder (id med "draft-"-prefix) indtil de har et
  // gyldigt lønart-nummer. Først da POSTes de. Eksisterende rækker persisteres
  // på blur — ikke pr. tastetryk — så vi undgår PUT-spam og nummer-kollisioner.
  const isDraft = (id: string) => id.startsWith('draft-');
  const isValidLonart = (n: string) => /^\d{1,4}$/.test(n);

  const patchLocal = (id: string, patch: Partial<DatalonSalaryType>) => {
    setSalaryTypes(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)));
  };

  const addType = () => {
    const draft: DatalonSalaryType = {
      id: `draft-${Date.now()}`,
      organizationId: '',
      lonartNumber: '',
      name: 'Ny lønart',
      description: null,
      computeRule: 'WORK_HOURS',
      sourceKind: 'ALL',
      defaultRate: 0,
      fixedAmount: null,
      sortOrder: salaryTypes.length,
      isActive: true,
      createdAt: '',
      updatedAt: '',
    };
    setSalaryTypes(prev => [...prev, draft]);
  };

  const toRate = (v: string | number | null): number =>
    v === null || v === '' ? 0 : typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.')) || 0;
  const toFixed = (v: string | number | null): number | null => {
    if (v === null || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return Number.isNaN(n) ? null : n;
  };

  const persistRow = async (row: DatalonSalaryType) => {
    if (isDraft(row.id)) {
      // Opret først når nummeret er gyldigt — ellers bliver kladden bare stående
      if (!isValidLonart(row.lonartNumber)) return;
      setSaving(true);
      try {
        const created = await api.createDatalonSalaryType({
          lonartNumber: row.lonartNumber,
          name: row.name.trim() || 'Ny lønart',
          description: row.description,
          computeRule: row.computeRule,
          sourceKind: row.sourceKind,
          defaultRate: toRate(row.defaultRate),
          fixedAmount: toFixed(row.fixedAmount),
          sortOrder: row.sortOrder,
          isActive: row.isActive,
        } as Omit<DatalonSalaryType, 'id' | 'createdAt' | 'updatedAt'>);
        setSalaryTypes(prev => prev.map(t => (t.id === row.id ? created : t)));
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Kunne ikke oprette lønart');
      } finally {
        setSaving(false);
      }
      return;
    }
    // Eksisterende række
    if (!isValidLonart(row.lonartNumber)) { setError('Lønart skal være 1-4 cifre'); return; }
    if (row.name.trim() === '') { setError('Navn må ikke være tomt'); return; }
    setSaving(true);
    try {
      const updated = await api.updateDatalonSalaryType(row.id, {
        lonartNumber: row.lonartNumber,
        name: row.name.trim(),
        computeRule: row.computeRule,
        sourceKind: row.sourceKind,
        defaultRate: toRate(row.defaultRate),
        fixedAmount: toFixed(row.fixedAmount),
        isActive: row.isActive,
      });
      setSalaryTypes(prev => prev.map(t => (t.id === row.id ? updated : t)));
      setError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Kunne ikke gemme lønart';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const deleteType = async (id: string) => {
    // Kladde: ikke gemt på serveren endnu — fjern bare lokalt
    if (isDraft(id)) {
      setSalaryTypes(prev => prev.filter(t => t.id !== id));
      return;
    }
    if (!window.confirm('Slet denne lønart? Alle chauffør-satser for den fjernes også.')) return;
    setSaving(true);
    try {
      await api.deleteDatalonSalaryType(id);
      setSalaryTypes(prev => prev.filter(t => t.id !== id));
      setDriverRates(prev => prev.filter(r => r.salaryTypeId !== id));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Kunne ikke slette lønart';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // Save all driver rate edits
  // ============================================================
  const saveRates = async () => {
    setSaving(true);
    try {
      for (const [key, value] of Object.entries(rateEdits)) {
        const [driverId, salaryTypeId] = key.split('|');
        const trimmed = value.trim();
        if (trimmed === '') {
          await api.deleteDatalonDriverRate(driverId, salaryTypeId);
        } else {
          const rate = parseFloat(trimmed.replace(',', '.'));
          if (Number.isNaN(rate) || rate < 0) continue;
          await api.upsertDatalonDriverRate(driverId, salaryTypeId, rate);
        }
      }
      setRateEdits({});
      await reload();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Kunne ikke gemme satser';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  }

  const hasRateChanges = Object.keys(rateEdits).length > 0;

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Lønart-katalog */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                Lønarter (Dataløn Branche)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Definér hvilke lønart-linjer der genereres pr. chauffør pr. eksport.
                Eksempel: 1002 Timer, 2001 Feriepenge, 3001 Personalekasse.
              </Typography>
            </Box>
            <Button size="small" startIcon={<AddIcon />} onClick={addType} disabled={saving}>
              Tilføj lønart
            </Button>
          </Box>

          {salaryTypes.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Ingen lønarter endnu — tilføj én for at komme i gang.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 90 }}>Lønart nr.</TableCell>
                    <TableCell>Navn</TableCell>
                    <TableCell sx={{ width: 200 }}>Beregning</TableCell>
                    <TableCell sx={{ width: 180 }}>Timetype</TableCell>
                    <TableCell sx={{ width: 110 }}>Fast antal</TableCell>
                    <TableCell sx={{ width: 130 }}>Default sats (kr)</TableCell>
                    <TableCell sx={{ width: 80 }}>Aktiv</TableCell>
                    <TableCell sx={{ width: 60 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salaryTypes.map(t => (
                    <TableRow key={t.id} hover>
                      <TableCell>
                        <TextField
                          size="small"
                          value={t.lonartNumber}
                          onChange={(e) => patchLocal(t.id, { lonartNumber: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                          onBlur={() => persistRow(t)}
                          placeholder="1002"
                          error={t.lonartNumber !== '' && !isValidLonart(t.lonartNumber)}
                          sx={{ '& .MuiInputBase-input': { py: 0.5, px: 1 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={t.name}
                          onChange={(e) => patchLocal(t.id, { name: e.target.value })}
                          onBlur={() => persistRow(t)}
                          sx={{ '& .MuiInputBase-input': { py: 0.5, px: 1 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={t.computeRule}
                            onChange={(e) => {
                              const computeRule = e.target.value as DatalonComputeRule;
                              patchLocal(t.id, { computeRule });
                              persistRow({ ...t, computeRule });
                            }}
                            sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                          >
                            {(Object.keys(COMPUTE_LABELS) as DatalonComputeRule[]).map(rule => (
                              <MenuItem key={rule} value={rule}>{COMPUTE_LABELS[rule]}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={t.sourceKind || 'ALL'}
                            disabled={t.computeRule !== 'WORK_HOURS'}
                            onChange={(e) => {
                              const sourceKind = e.target.value as DatalonSourceKind;
                              patchLocal(t.id, { sourceKind });
                              persistRow({ ...t, sourceKind });
                            }}
                            sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                          >
                            {(Object.entries(SOURCE_KIND_LABELS) as [DatalonSourceKind, string][]).map(([k, label]) => (
                              <MenuItem key={k} value={k}>{label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {/* Feltet er kun meningsfuldt for FIXED_PER_PERIOD — men et
                            deaktiveret felt uden forklaring ligner en fejl. Sig hvorfor. */}
                        <Tooltip
                          title={
                            t.computeRule === 'FIXED_PER_PERIOD'
                              ? 'Antallet der afregnes pr. lønperiode, fx 2 udkald'
                              : 'Sæt Beregning til "Fast antal pr. periode" for at kunne udfylde her'
                          }
                        >
                          <span>
                            <TextField
                              size="small"
                              disabled={t.computeRule !== 'FIXED_PER_PERIOD'}
                              value={t.fixedAmount !== null && t.fixedAmount !== undefined ? num(t.fixedAmount) : ''}
                              onChange={(e) => patchLocal(t.id, { fixedAmount: e.target.value === '' ? null : e.target.value })}
                              onBlur={() => persistRow(t)}
                              placeholder={t.computeRule === 'FIXED_PER_PERIOD' ? '0' : 'kræver fast antal'}
                              sx={{ '& .MuiInputBase-input': { py: 0.5, px: 1 } }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={num(t.defaultRate)}
                          onChange={(e) => patchLocal(t.id, { defaultRate: e.target.value })}
                          onBlur={() => persistRow(t)}
                          InputProps={{ endAdornment: <InputAdornment position="end">kr</InputAdornment> }}
                          sx={{ '& .MuiInputBase-input': { py: 0.5, px: 1 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={t.isActive}
                          onChange={(e) => {
                            const isActive = e.target.checked;
                            patchLocal(t.id, { isActive });
                            persistRow({ ...t, isActive });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Slet lønart">
                          <IconButton size="small" onClick={() => deleteType(t.id)} disabled={saving}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Chauffør-sats-matrix */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                Chauffør-satser
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Override default-satsen pr. chauffør pr. lønart. Tom celle = brug default fra tabellen ovenfor.
              </Typography>
            </Box>
            {mappingsDirty && (
              <Button
                variant="outlined"
                size="small"
                startIcon={savingMappings ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={() => onSaveMappings?.()}
                disabled={savingMappings}
                sx={{ mr: 1 }}
              >
                Gem medarbejdernr
              </Button>
            )}
            {hasRateChanges && (
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={saveRates}
                disabled={saving}
              >
                Gem satser ({Object.keys(rateEdits).length})
              </Button>
            )}
          </Box>

          {activeTypes.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>Opret mindst én aktiv lønart for at sætte chauffør-satser.</Alert>
          ) : driverMappings.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>Ingen chauffører fundet. Synkroniser fra DORA først.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 180, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                      Chauffør
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <Box>
                        <Typography variant="caption" fontWeight={600} display="block">Dataløn medarbejdernr</Typography>
                        <Typography variant="caption" color="text.secondary">Bruges i CSV-eksport. Tom = fald tilbage til Danløn-nr.</Typography>
                      </Box>
                    </TableCell>
                    {activeTypes.map(t => (
                      <TableCell key={t.id} align="right" sx={{ minWidth: 110 }}>
                        <Box>
                          <Typography variant="caption" fontWeight={600} display="block">{t.lonartNumber}</Typography>
                          <Typography variant="caption" color="text.secondary">{t.name}</Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {driverMappings.map(dm => (
                    <TableRow key={dm.driverId} hover>
                      <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                        {dm.driverName}
                        {!dm.employeeNumber && !dm.datalonEmployeeNumber && (
                          <Chip size="small" label="Mangler medarbejdernr." color="warning" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={dm.datalonEmployeeNumber || ''}
                          onChange={(e) => onMappingChange?.(dm.driverId, 'datalonEmployeeNumber', e.target.value)}
                          placeholder={dm.employeeNumber || '—'}
                          disabled={!onMappingChange}
                          sx={{ width: 130, '& .MuiInputBase-input': { py: 0.5, px: 1 } }}
                        />
                      </TableCell>
                      {activeTypes.map(t => (
                        <TableCell key={t.id}>
                          <TextField
                            size="small"
                            value={getDriverRate(dm.driverId, t.id)}
                            onChange={(e) => setDriverRateEdit(dm.driverId, t.id, e.target.value)}
                            placeholder={num(t.defaultRate)}
                            InputProps={{ endAdornment: <InputAdornment position="end">kr</InputAdornment> }}
                            sx={{ width: 120, '& .MuiInputBase-input': { py: 0.5, px: 1, textAlign: 'right' } }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
